import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Stack,
  TableSortLabel,
  LinearProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import { useBookcard } from './hooks/useBookcard';
import { useBookcardSet, formatPrset, isBlocked, currentPrset } from './hooks/useBookcardSet';
import BioEditorDialog from './components/BioEditorDialog';

const EAN_PATTERN = /^\d{13}$/;

function SingleEanTab() {
  const [ean, setEan] = useState('');
  const { generateBookcard, loading, error, lastGenerated } = useBookcard();
  const isValid = EAN_PATTERN.test(ean.trim());

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid) generateBookcard(ean.trim());
  };

  return (
    <Box sx={{ maxWidth: 480, mt: 3 }}>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <TextField
          label="EAN-13"
          value={ean}
          onChange={(e) => setEan(e.target.value)}
          inputProps={{ maxLength: 13, inputMode: 'numeric', pattern: '[0-9]*' }}
          disabled={loading}
          error={ean.length > 0 && !isValid}
          helperText={ean.length > 0 && !isValid ? '13 digits required' : ' '}
          autoFocus
          sx={{ flex: 1 }}
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          sx={{ mt: 0.25 }}
        >
          {loading ? 'Generating…' : 'Generate'}
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {lastGenerated && !error && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
          {lastGenerated}-bookcard.pdf downloaded
        </Alert>
      )}
    </Box>
  );
}

function ProductionSetTab() {
  const [selectedPrset, setSelectedPrset] = useState('');
  const {
    prsets, presetsLoading, presetsError,
    products, productsLoading, productsError, loadProducts,
    zipLoading, zipError, zipProgress, zipDownloadUrl, downloadZip,
  } = useBookcardSet();
  const { generateBookcard, loading: singleLoading } = useBookcard();
  const [generatingEan, setGeneratingEan] = useState(null);
  const [editingEan, setEditingEan] = useState(null);
  const [bioOverridesByEan, setBioOverridesByEan] = useState({});
  const [sortBy, setSortBy] = useState('notes');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    if (prsets && prsets.length > 0 && selectedPrset === '') {
      const preset = currentPrset(prsets);
      if (preset) setSelectedPrset(preset);
    }
  }, [prsets, selectedPrset]);

  const handleLoad = () => {
    if (selectedPrset) loadProducts(selectedPrset);
  };

  const handleSingle = async (ean) => {
    setGeneratingEan(ean);
    await generateBookcard(ean, bioOverridesByEan[ean]);
    setGeneratingEan(null);
  };

  const handleSaveBios = (ean, bios) => {
    setBioOverridesByEan((prev) => ({ ...prev, [ean]: bios }));
  };

  const handleZip = () => {
    if (!products) return;
    const eans = products.filter((p) => !isBlocked(p)).map((p) => p.ean);
    downloadZip(eans, selectedPrset);
  };

  const creatableCount = products ? products.filter((p) => !isBlocked(p)).length : 0;

  const noteCount = (p) => {
    let count = 0;
    if (isBlocked(p)) count += 1;
    if (p.needs_editing && !bioOverridesByEan[p.ean]) count += 1;
    if (bioOverridesByEan[p.ean]) count += 1;
    return count;
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      // Notes default to showing flagged products first; other columns default alphabetical/ascending.
      setSortDirection(column === 'notes' ? 'desc' : 'asc');
    }
  };

  const sortedProducts = products ? [...products].sort((a, b) => {
    let cmp = 0;
    switch (sortBy) {
      case 'title':
        cmp = (a.title || '').localeCompare(b.title || '');
        break;
      case 'ean':
        cmp = (a.ean || '').localeCompare(b.ean || '');
        break;
      case 'access':
        cmp = (a.open_access === 'Y' ? 0 : 1) - (b.open_access === 'Y' ? 0 : 1);
        break;
      case 'notes':
        cmp = noteCount(a) - noteCount(b);
        break;
      default:
        cmp = 0;
    }
    return sortDirection === 'asc' ? cmp : -cmp;
  }) : [];

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <FormControl sx={{ minWidth: 240 }} disabled={presetsLoading}>
          <InputLabel id="prset-label">Production Set</InputLabel>
          <Select
            labelId="prset-label"
            label="Production Set"
            value={selectedPrset}
            onChange={(e) => setSelectedPrset(e.target.value)}
          >
            {prsets.map((p) => (
              <MenuItem key={p} value={p}>{formatPrset(p)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleLoad}
          disabled={!selectedPrset || productsLoading}
          startIcon={productsLoading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {productsLoading ? 'Loading…' : 'Load'}
        </Button>
        {products && (
          <Button
            variant="outlined"
            onClick={handleZip}
            disabled={zipLoading || creatableCount === 0}
            startIcon={zipLoading ? <CircularProgress size={18} color="inherit" /> : <FolderZipIcon />}
            sx={{ ml: 'auto' }}
          >
            {zipLoading && zipProgress
              ? `Generating… ${zipProgress.completed} / ${zipProgress.total}`
              : `Generate All (${creatableCount})`}
          </Button>
        )}
      </Box>

      {zipLoading && zipProgress && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip label="Generating ZIP" color="primary" size="small" />
            <Typography variant="body2" color="text.secondary">
              {zipProgress.completed} / {zipProgress.total} bookcards
            </Typography>
          </Box>
          <LinearProgress
            variant={zipProgress.total > 0 ? 'determinate' : 'indeterminate'}
            value={zipProgress.total > 0 ? (100 * zipProgress.completed) / zipProgress.total : 0}
          />
        </Box>
      )}

      {zipDownloadUrl && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ mb: 3 }}
          action={
            <Button
              component="a"
              href={zipDownloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="contained"
              startIcon={<DownloadIcon />}
            >
              Download ZIP
            </Button>
          }
        >
          ZIP ready — {selectedPrset} ({zipProgress?.total ?? ''} bookcards)
        </Alert>
      )}

      {presetsError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load sets: {presetsError}</Alert>}
      {productsError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load products: {productsError}</Alert>}
      {zipError && <Alert severity="error" sx={{ mb: 2 }}>ZIP error: {zipError}</Alert>}

      {products && products.length === 0 && (
        <Alert severity="info">No products found in this set.</Alert>
      )}

      {products && products.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'title'}
                    direction={sortBy === 'title' ? sortDirection : 'asc'}
                    onClick={() => handleSort('title')}
                  >
                    <strong>Title</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'ean'}
                    direction={sortBy === 'ean' ? sortDirection : 'asc'}
                    onClick={() => handleSort('ean')}
                  >
                    <strong>EAN</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'access'}
                    direction={sortBy === 'access' ? sortDirection : 'asc'}
                    onClick={() => handleSort('access')}
                  >
                    <strong>Access</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'notes'}
                    direction={sortBy === 'notes' ? sortDirection : 'desc'}
                    onClick={() => handleSort('notes')}
                  >
                    <strong>Notes</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedProducts.map((p) => {
                const blocked = isBlocked(p);
                const isGenerating = generatingEan === p.ean;
                return (
                  <TableRow key={p.ean} sx={blocked ? { opacity: 0.6 } : {}}>
                    <TableCell>
                      <Typography variant="body2">{p.title || '—'}</Typography>
                      {p.subtitle && (
                        <Typography variant="caption" color="text.secondary">{p.subtitle}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.ean}</Typography>
                    </TableCell>
                    <TableCell>
                      {p.open_access === 'Y' ? (
                        <Chip
                          icon={<LockOpenIcon />}
                          label="Open Access"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<LockIcon />}
                          label="Paywall"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="column" spacing={0.5} alignItems="flex-start">
                        {blocked && (
                          <Chip
                            icon={<WarningAmberIcon />}
                            label="No price set"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {p.needs_editing && !bioOverridesByEan[p.ean] && (
                          <Chip
                            icon={<WarningAmberIcon />}
                            label="Text may be too long"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {bioOverridesByEan[p.ean] && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Edited"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setEditingEan(p.ean)}
                          startIcon={<EditIcon fontSize="small" />}
                          sx={{ borderRadius: 999, px: 2 }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={blocked || isGenerating || singleLoading}
                          onClick={() => handleSingle(p.ean)}
                          startIcon={isGenerating ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                          sx={{ borderRadius: 999, px: 2 }}
                        >
                          Generate
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <BioEditorDialog
        ean={editingEan}
        open={editingEan != null}
        initialBios={editingEan ? bioOverridesByEan[editingEan] : undefined}
        onSave={handleSaveBios}
        onClose={() => setEditingEan(null)}
      />
    </Box>
  );
}

function BookcardGenerator() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom>
        Bookcard Generator
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Production Set" />
        <Tab label="Single EAN" />
      </Tabs>
      {tab === 0 && <ProductionSetTab />}
      {tab === 1 && <SingleEanTab />}
    </Box>
  );
}

export default BookcardGenerator;
